import { useQuery } from '@tanstack/react-query';
import { listChromeAlerts } from '@/api/real-browsers';
import type { AlertRecord } from '@/types';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';

const SEV_TONE: Record<AlertRecord['severity'], string> = {
  info:     'bg-sky-500/10 text-sky-300 ring-sky-500/30',
  warn:     'bg-yellow-500/10 text-yellow-300 ring-yellow-500/30',
  error:    'bg-orange-500/10 text-orange-300 ring-orange-500/30',
  critical: 'bg-red-500/15 text-red-300 ring-red-500/40',
};

/**
 * Alerts page — pre-filtered to category `infra.chrome`. Same backend
 * collection as observe's /alerts (one source of truth), so the auto-
 * healer + Slack integration the platform already has applies here.
 */
export default function Alerts() {
  const { data: alerts = [], isLoading, error } = useQuery({
    queryKey: ['chrome-alerts'],
    queryFn: () => listChromeAlerts(100),
    refetchInterval: 3_000,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Alerts</h1>
        <p className="text-sm text-slate-500">
          Live <code className="font-mono">infra.chrome</code> alerts. Same
          stream Slack sees.
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
          failed to load alerts: {(error as Error).message}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-500">loading…</div>
      ) : alerts.length === 0 ? (
        <div className="text-sm text-slate-500">No infra.chrome alerts in the window.</div>
      ) : (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl divide-y divide-slate-800">
          {alerts.map(a => (
            <div key={a.id} className="p-3 flex items-start gap-3">
              <span className={cn('text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 shrink-0 mt-0.5', SEV_TONE[a.severity])}>
                {a.severity}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm">{a.message}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {a.category} · {relativeTime(a.ts)}
                  {a.count && a.count > 1 && <> · ×{a.count}</>}
                </div>
                {a.context && Object.keys(a.context).length > 0 && (
                  <pre className="mt-1.5 text-[11px] text-slate-400 bg-slate-950/60 p-2 rounded overflow-x-auto">
                    {JSON.stringify(a.context, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
